import React, { useState } from "react";
import { MapPin, Navigation, Phone, Building2, Package, ChevronDown, ChevronUp, User } from "lucide-react";
import { mapsDirUrl, telHref } from "@/lib/maps";

const fmtAddr = (a) => [a.line1, a.line2, a.line1 === a.city ? "" : a.city].filter(Boolean).join(", ") + (a.postcode ? ` ${a.postcode}` : "");

export const currentLeg = (status) => (["picked_up", "in_transit", "delivered", "completed"].includes(status) ? "delivery" : "pickup");

export function ConsignmentItems({ job, testid }) {
  const items = job.items?.length ? job.items : [{ size: job.size, quantity: 1, weight_kg: job.weight_kg, parcel_description: job.parcel_description }];
  return (
    <ul className="space-y-0.5 text-sm" data-testid={testid}>
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-1.5">
          <Package className="mt-0.5 h-3.5 w-3.5 shrink-0 text-kilted-gold" />
          <span><b>{it.quantity || 1}×</b> <span className="capitalize">{it.size}</span>{it.weight_kg ? ` · ${it.weight_kg}kg` : ""}{it.parcel_description ? <span className="text-muted-foreground"> — {it.parcel_description}</span> : ""}</span>
        </li>
      ))}
    </ul>
  );
}

// The stop the driver is working on right now: PICKUP until the parcel is on board, then DELIVERY.
export default function JobLeg({ job }) {
  const leg = currentLeg(job.status);
  const a = leg === "pickup" ? job.pickup : job.dropoff;
  const other = leg === "pickup" ? job.dropoff : job.pickup;
  const [showOther, setShowOther] = useState(false);
  const t = job.tracking_number;
  const dir = job.directory_notes?.[leg];
  const contactName = a.contact_name || dir?.contact_name;
  const contactPhone = a.contact_phone || dir?.contact_phone;
  return (
    <div className="space-y-2" data-testid={`job-leg-${t}`}>
      <div className={`rounded-md border-l-4 p-3 ${leg === "pickup" ? "border-kilted-green bg-kilted-green/5" : "border-kilted-gold bg-kilted-gold/10"}`}>
        <p className={`text-[11px] font-bold uppercase tracking-wide ${leg === "pickup" ? "text-kilted-green" : "text-amber-800"}`} data-testid={`job-leg-label-${t}`}>
          {leg === "pickup" ? "Pick up from" : "Deliver to"}
        </p>
        <p className="mt-0.5 flex items-center gap-1.5 text-base font-bold" data-testid={`job-leg-business-${t}`}>
          <Building2 className="h-4 w-4 shrink-0 text-kilted-gold" />{a.business || dir?.trade_name || <span className="font-medium italic text-muted-foreground">Residential</span>}
        </p>
        <a href={mapsDirUrl(a)} target="_blank" rel="noreferrer" data-testid={`job-leg-maps-${t}`}
          className="mt-1 flex items-start gap-1.5 font-medium text-kilted-green underline underline-offset-2 hover:text-kilted-greenLight">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0" /><span>{fmtAddr(a)}</span><Navigation className="mt-1 h-3.5 w-3.5 shrink-0" />
        </a>
        <p className="mt-1 flex items-center gap-1.5 text-sm" data-testid={`job-leg-contact-${t}`}>
          <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          {contactName || contactPhone ? (
            <span>{contactName || "Contact"}{contactPhone && <> · <a href={telHref(contactPhone)} className="inline-flex items-center gap-1 font-semibold text-kilted-green hover:underline"><Phone className="h-3 w-3" />{contactPhone}</a></>}</span>
          ) : <span className="text-muted-foreground">No contact on file</span>}
        </p>
        {a.customer_reference && <p className="mt-0.5 text-xs font-medium text-kilted-green">Ref: {a.customer_reference}</p>}
        <div className="mt-2 border-t border-black/5 pt-2">
          <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Consignment</p>
          <ConsignmentItems job={job} testid={`job-leg-items-${t}`} />
        </div>
      </div>
      <button type="button" onClick={() => setShowOther((v) => !v)} data-testid={`job-leg-other-toggle-${t}`}
        className="flex w-full items-center justify-between rounded-md px-1 py-1 text-left text-xs text-muted-foreground hover:text-kilted-green">
        <span>{leg === "pickup" ? "Then deliver to" : "Picked up from"}: <b className="text-foreground">{other.business || other.line1}</b>{other.business || other.line1 !== other.city ? `, ${other.city}` : ""}</span>
        {showOther ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {showOther && (
        <div className="rounded-md bg-secondary/50 p-3 text-sm" data-testid={`job-leg-other-${t}`}>
          <p className="font-medium">{other.business || <span className="italic text-muted-foreground">Residential</span>}</p>
          <a href={mapsDirUrl(other)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-kilted-green underline underline-offset-2">{fmtAddr(other)}<Navigation className="h-3.5 w-3.5" /></a>
          {other.contact_phone && <a href={telHref(other.contact_phone)} className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><Phone className="h-3 w-3" />{other.contact_name ? `${other.contact_name} · ` : ""}{other.contact_phone}</a>}
        </div>
      )}
    </div>
  );
}
